import React, {
  ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  LazyLoadImage,
  LazyLoadImageProps,
} from 'react-lazy-load-image-component';
import styles from './image.module.css';
import useMeasure from 'react-use-measure';

/* eslint-disable-next-line */
export interface ImageProps {
  /**
   * @param lazyLoad 是否开启懒加载
   */
  lazyLoad?: boolean | LazyLoadImageProps;
  /**
   * @param compress 是否开启二次压缩
   */
  compress?: boolean;
  src: string;
  /**
   * @param sources 设置 picture 元素内的 source 元素，由低质量到高质量。
   * @param srcsets 设置 source 元素的 srcset 属性值，由1倍开始。
   * @param media 设置 source 元素的 media 属性，即媒体查询条件，例如：(max-width: 768px)。
   */
  sources?: {
    srcsets: string[];
    media?: string;
  }[];
  alt?: string;
  style?: React.CSSProperties;
  className?: string;
  imgClassName?: string;
  detail?: {
    button: ReactNode;
    image: ImageProps;
  };
}

export interface ImageHandle {
  getDOMNode: () =>
    | HTMLHeadingElement
    | HTMLParagraphElement
    | HTMLSpanElement
    | null;
  seeHello: () => void;
}

interface detailImgMeasureProps {
  width: number;
  height: number;
  top: number;
  left: number;
  right: number;
  bottom: number;
}

const formatUrl = (url: string, action: 'compress' | 'original'): string => {
  if (action === 'compress') {
    if (url.includes('zhiyun-website-shenzhen.oss-cn-shenzhen.aliyuncs.com')) {
      return formatUrl(
        url.replace(
          'zhiyun-website-shenzhen.oss-cn-shenzhen.aliyuncs.com',
          'bucket.zhiyun-tech.com'
        ),
        action
      );
    } else {
      return url;
    }
  } else if (action === 'original') {
    if (url.includes('bucket.zhiyun-tech.com')) {
      return formatUrl(
        url.replace(
          'bucket.zhiyun-tech.com',
          'zhiyun-website-shenzhen.oss-cn-shenzhen.aliyuncs.com'
        ),
        action
      );
    } else {
      return url;
    }
  } else {
    console.warn(`action 应该为 compress | original，但获取到 ${action}`);
    return url;
  }
};

export const Image = forwardRef<ImageHandle, ImageProps>(
  (props: ImageProps, ref) => {
    const { lazyLoad, compress, src, sources, alt, style, className, detail } =
      props;

    const innerRef = useRef<HTMLImageElement>(null);
    const [threeCtnRef, option] = useMeasure();
  

    useImperativeHandle(ref, () => ({
      getDOMNode: () => innerRef.current,
      seeHello: () => {
        console.log('Hello,World!');
      },
    }));
    const getPictureJSXNode = (): JSX.Element => {
      if (sources) {
        return (
          <picture
            style={{
              display: 'block',
            }}
            ref={innerRef}
          >
            {sources.map((source) => {
              return (
                <source
                  srcSet={formatUrl(
                    source.srcsets
                      .map((srcset, index) => `${srcset} ${index + 1}x`)
                      .join(', '),
                    compress ? 'compress' : 'original'
                  )}
                  media={source.media}
                />
              );
            })}

            {lazyLoad ? (
              <LazyLoadImage
                alt={alt}
                src={formatUrl(src, compress ? 'compress' : 'original')}
                className={className}
                style={{
                  display: 'block',
                  ...style,
                }}
              />
            ) : (
              <img
                ref={innerRef}
                alt={alt}
                src={formatUrl(src, compress ? 'compress' : 'original')}
                className={className}
                style={{
                  display: 'block',
                  ...style,
                }}
              />
            )}
          </picture>
        );
      } else {
        return lazyLoad ? (
          <picture ref={innerRef}>
            <LazyLoadImage
              alt={alt}
              src={formatUrl(src, compress ? 'compress' : 'original')}
              className={className}
              style={{
                display: 'block',
                ...style,
              }}
            />
          </picture>
        ) : (
          <img
            ref={innerRef}
            alt={alt}
            src={formatUrl(src, compress ? 'compress' : 'original')}
            className={className}
            style={{
              display: 'block',
              ...style,
            }}
          />
        );
      }
    };

    return detail ? (
      <div className={styles.wrap}>
      <div ref={threeCtnRef} className={styles.threeCtn} onClick={(e) => {

        const element = e.currentTarget
        element.classList.toggle(styles.active)
      }}>
        {getPictureJSXNode()}
        <div className={styles.detail} style={{
          left:-option.left,
          top:`calc(-1 * ((100vh - ${option.height}px) / 2))`
        } }>{<Image {...detail.image} style={{
          width:option.width
        }} className={styles.detailImg}/>}</div>
      </div>
      </div>

    ) : (
      getPictureJSXNode()
    );
  }
);

export default Image;
